import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getUserProfile, updateUserProfile, changePassword, initiateEmailChange, cancelEmailChange, verifyEmailChange, getEmailChangeStatus, completeEmailChange, getUserProfileSettings, updateUserProfileSettings } from "../api/user";
import UserProfile, { UserProfileSettings } from "../models/user/UserProfile";

const userKeys = {
  profile: ["user", "profile"] as const,
  profileSettings: ["user", "profile", "settings"] as const,
  emailChangeStatus: ["user", "emailChangeStatus"] as const,
};

export function useUserProfile(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.profile,
    queryFn: (): Promise<UserProfile> => getUserProfile(),
    staleTime: 60_000,
    enabled: options?.enabled ?? false,
  });
}

export function useUserProfileSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.profileSettings,
    queryFn: (): Promise<UserProfileSettings> => getUserProfileSettings(),
    staleTime: 60_000,
    enabled: options?.enabled ?? false
  });
}

export function useUpdateUserProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (profile: UserProfile) => updateUserProfile(profile),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.profile as unknown as string[] });
    },
  });
}

export function useUpdateUserProfileSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (profileSettings: UserProfileSettings) => updateUserProfileSettings(profileSettings),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.profileSettings as unknown as string[]})
    }
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      changePassword(currentPassword, newPassword),
  });
}

export function useInitiateEmailChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ newEmail, currentPassword, verificationCallbackUrl }: { newEmail: string; currentPassword: string; verificationCallbackUrl: string }) =>
      initiateEmailChange(newEmail, currentPassword, verificationCallbackUrl),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.emailChangeStatus as unknown as string[] });
    },
  });
}

export function useCancelEmailChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cancelEmailChange(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.emailChangeStatus as unknown as string[] });
    },
  });
}

export function useEmailChangeStatus() {
  return useQuery({
    queryKey: userKeys.emailChangeStatus,
    queryFn: () => getEmailChangeStatus(),
    staleTime: 30_000,
  });
}

export function useVerifyEmailChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ token, type }: { token: string; type: "old" | "new" }) =>
      verifyEmailChange(token, type),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.emailChangeStatus as unknown as string[] });
    },
  });
}

export function useCompleteEmailChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => completeEmailChange(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: userKeys.emailChangeStatus as unknown as string[] });
      qc.invalidateQueries({ queryKey: userKeys.profile as unknown as string[] });
    },
  });
}
